import React from 'react';
import { Form, Select, Typography, Row, Col } from 'antd';
import LicenseUpgradeModal from '../LicenseUpgradeModal';
import Tutorial from './Tutorial';
import { variantTokens } from '../../../theme/theme';
import LicenseOptionRenderer from '../LicenseOptionRenderer';

const { Title, Text } = Typography;

const LicenseStep = ({ form, availableLicenses, loadingLicenses, handleFormChange, setUpgradeModal, upgradeModal }) => {

    // Function to sort licenses by subscription type
    const sortLicensesByType = (licenses) => {
        return [...licenses].sort((a, b) => {
            const getTypeCategory = (subscriptionType) => {
                if (!subscriptionType) return 999; // Put unknown types at the end

                const type = subscriptionType.toLowerCase();

                // Check in priority order - additional has highest priority (overrides everything)
                if (type.includes('additional')) {
                    return 2; // Additional licenses last (they are add-ons regardless of other keywords)
                }
                if (type.includes('branded')) {
                    return 0; // Pure branded licenses first
                }
                if (type.includes('smart')) {
                    return 1; // Pure smart licenses second
                }

                return 999; // Unknown types at the end
            };

            const categoryA = getTypeCategory(a.subscriptionType);
            const categoryB = getTypeCategory(b.subscriptionType);

            // If same category, sort by name for consistent ordering
            if (categoryA === categoryB) {
                return (a.name || '').localeCompare(b.name || '');
            }

            return categoryA - categoryB;
        });
    };

    // Filter out gateway licenses and then sort
    const filteredLicenses = availableLicenses.filter(license => {
        const subscriptionType = license.subscriptionType?.toLowerCase() || '';
        return !subscriptionType.includes('gateway');
    });

    const sortedLicenses = sortLicensesByType(filteredLicenses);

    return (
        <Row gutter={32} style={{ minHeight: 400 }}>
            {/* Left: Form */}
            <Col xs={24} md={12}>
                <div style={{ padding: '1.25rem 1rem' }}>
                    <Title level={4}>Select Available License</Title>
                    <Text type="secondary">
                        Choose a license for your catalogue. This will determine what features are available.
                    </Text>

                    <Form
                        form={form}
                        layout="vertical"
                        style={{ marginTop: '1.5rem' }}
                        onValuesChange={handleFormChange}
                    >
                        <Form.Item
                            name="licenseList"
                            label="License"
                            rules={[{ required: true, message: 'Please select a license' }]}
                        >
                            <Select
                                placeholder="Select available license"
                                loading={loadingLicenses}
                                optionLabelProp="label"
                                showSearch
                                filterOption={(input, option) =>
                                    option?.label?.toLowerCase().includes(input.toLowerCase())
                                }
                            >
                                {sortedLicenses.map(license => {
                                    return (
                                        <Select.Option
                                            key={license.id}
                                            value={license.id}
                                            label={license.name}
                                        >
                                            <LicenseOptionRenderer
                                                license={license}
                                            />
                                        </Select.Option>
                                    );
                                })}
                            </Select>
                        </Form.Item>
                    </Form>
                    <LicenseUpgradeModal
                        visible={upgradeModal.visible}
                        onClose={() => setUpgradeModal({ visible: false, license: null })}
                        license={upgradeModal.license}
                    />
                    <div style={{ marginTop: '1.5rem' }}>
                        <Text style={{ color: variantTokens.accent }}>
                            If you don't have a license, please go to the license page to purchase one.
                        </Text>
                    </div>
                </div>
            </Col>
            {/* Right: Tutorial */}
            <Col xs={24} md={12} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'start' }}>
                <Tutorial
                    title="License Selection"
                    description={
                        <>
                            Select the license that best fits your catalogue's needs.<br />
                            Your license determines available features, catalogue limits, and upgrade options.<br /><br />
                            <b>Tips:</b>
                            <ul style={{ paddingLeft: 20, margin: 0 }}>
                                <li>Check license limits before selecting</li>
                                <li>Upgrade your license to unlock more features or catalogues</li>
                                <li>Contact support if you need a custom solution</li>
                            </ul>
                        </>
                    }
                />
            </Col>
        </Row>
    );
};

export default LicenseStep;
